import getRtcStatsReportAdapterDefault from './get_rtc_stats_report_adapter';
import Errors from '../Errors';
import ExceptionCodes from '../exception_codes';
import OTErrorClass from '../ot_error_class';
import OTHelpersError from '../../common-js-helpers/error';

const minIntervalBetweenReportsMs = 1000;

function StatsCache(deps = {}) {
  const getRtcStatsReportAdapter = deps.getRtcStatsReportAdapter || getRtcStatsReportAdapterDefault;
  let lastRtcStatsReport = new WeakMap();
  let pendingRequests = new WeakMap();

  this.setPeerConnection = (peerConnection) => {
    if (!peerConnection || !peerConnection.getStats) {
      // this peerConnection doesn't support getStats, bail
      return;
    }
    this.peerConnection = peerConnection;
  };

  // source is a string identifying the requester of the stats report
  this.getRtcStatsReport = (track, source) => {
    if (!this.peerConnection) {
      const errorCode = ExceptionCodes.PEER_CONNECTION_NOT_CONNECTED;
      const error = new OTHelpersError(OTErrorClass.getTitleByCode(errorCode),
        Errors.PEER_CONNECTION_NOT_CONNECTED, {
          code: errorCode,
        });
      return Promise.reject(error);
    }
    const key = track || this.peerConnection;
    const now = Date.now();
    const lastReport = lastRtcStatsReport.get(key);
    if (lastReport &&
      !lastReport.sources.includes(source) && // We check the source because we don't want to return the same report to the same requester
      now - lastReport.timestamp < minIntervalBetweenReportsMs
    ) {
      lastReport.sources.push(source);
      return Promise.resolve(lastReport.stats);
    }
    const pendingRequest = pendingRequests.get(key);
    // We check the source because we don't want to return the same report to the same requester
    if (pendingRequest && !pendingRequest.sources.includes(source)) {
      pendingRequest.sources.push(source);
      return pendingRequest.promise;
    }
    // We are requesting a new report, delete last cached one
    lastRtcStatsReport.delete(key);
    const promise = new Promise((resolve, reject) => {
      getRtcStatsReportAdapter(this.peerConnection, track, (err, stats) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(stats);
      });
    }).then((stats) => {
      pendingRequests.delete(key);
      lastRtcStatsReport.set(key, {
        timestamp: Date.now(),
        stats,
        sources: [source],
      });
      return stats;
    }).catch((err) => {
      pendingRequests.delete(key);
      throw err;
    });
    pendingRequests.set(key, { promise, sources: [source] });
    return promise;
  };

  this.destroy = () => {
    this.peerConnection = null;
    lastRtcStatsReport = null;
    pendingRequests = null;
  };
}

export default StatsCache;
